import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, AlertTriangle, XCircle } from "lucide-react";

const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;

// Dialog storno a doppia conferma. NON duplica logica: usa POST /orders/{id}/reverse (FASE 6/7).
export default function StornoDialog({ open, onOpenChange, order, onDone }) {
  const [reason, setReason] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setStep(1); setBusy(false); };
  const close = () => { onOpenChange(false); reset(); };

  const proceed = () => {
    if (!reason.trim()) { toast.error("Il motivo dello storno è obbligatorio"); return; }
    setStep(2);
  };

  const doStorno = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/reverse`, { reason: reason.trim() });
      toast.success("Ordine stornato · magazzino ripristinato");
      if (data.payment_alert) toast.warning(data.payment_alert, { duration: 8000 });
      close();
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="storno-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="w-5 h-5" /> Storna ordine {order?.order_code}
          </DialogTitle>
          <DialogDescription className="text-sm pt-1">
            Lo storno ripristina il magazzino e annulla la vendita (ricavo e food cost). Il dato storico viene conservato: l'ordine non viene cancellato.
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Totale</span><span className="font-medium">{eur(order?.total)}</span></div>
              {order?.payment_status && <div className="flex justify-between"><span className="text-muted-foreground">Pagamento</span><span>{order.payment_status}</span></div>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Motivo dello storno (obbligatorio)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. errore di battitura, ordine non servito…" data-testid="storno-reason" />
            </div>
            {order?.payment_status === "paid" && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-800 p-2.5 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Il pagamento risulta ancora <b>acquisito</b>: dopo lo storno andrà gestito il rimborso separatamente. Nessun rimborso viene simulato.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2" data-testid="storno-confirm2">
            <p className="font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Conferma definitiva</p>
            <p>Stai per <b>stornare</b> l'ordine <b>{order?.order_code}</b> ({eur(order?.total)}).</p>
            <p className="text-muted-foreground">Motivo: «{reason.trim()}»</p>
            <p>Questa azione ripristina il magazzino e annulla la vendita. Confermi?</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 1 ? (
            <>
              <Button variant="outline" className="rounded-full" onClick={close} disabled={busy} data-testid="storno-cancel">Annulla</Button>
              <Button variant="destructive" className="rounded-full" onClick={proceed} disabled={busy} data-testid="storno-proceed">Procedi</Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(1)} disabled={busy} data-testid="storno-back">Indietro</Button>
              <Button variant="destructive" className="rounded-full" onClick={doStorno} disabled={busy} data-testid="storno-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma storno"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
